import React, { useEffect, useState } from "react";
import { ArrowRight, Check, Lock } from "lucide-react";
import { api } from "@/lib/api";
import PublicLayout, { Eyebrow, PrimaryBtn, GhostBtn, H2 } from "@/components/public/PublicLayout";
import { ROUTES, C, SERIF } from "@/lib/publicLinks";
import { DEFAULT_PAGES, useSiteContent } from "@/lib/siteContent";
import { DIRECTED_TREATMENTS, HSA_NOTE, existingDetails, splitClinical } from "@/lib/serviceCategories";

// Static fallback (used only if the offers API is unavailable) — nothing disappears.
const FALLBACK_SERVICES = [
  { title: "KISSED Hair Loss Investigation™", short_description: "Comprehensive hair & scalp investigation with history review, microscopic analysis, and personalized next steps." },
  { title: "Microscopic Hair & Scalp Analysis", short_description: "A focused, magnified assessment of the hair and scalp with personalized recommendations." },
  { title: "KISSED Wellness Vital Check™", short_description: "An in-clinic wellness snapshot to help identify patterns worth a closer look." },
  { title: "Biometric Wellness Analysis — Powered by ZYTO", short_description: "Biometric wellness screening used as part of a broader, non-diagnostic wellness picture." },
  { title: "Women's Wellness Consultation", short_description: "A personalized in-clinic conversation about energy, sleep, stress, weight, hair, and overall wellness." },
  { title: "KISSED Herbal Yoni Steam", short_description: "A traditional herbal steam wellness ritual offered in a private, comfortable setting." },
  { title: "Herbal Foot Wellness Soak", short_description: "A restorative herbal foot soak to support relaxation and grounding." },
  { title: "Herbal Hand Wellness Soak", short_description: "A soothing herbal hand soak as part of a calming wellness experience." },
  { title: "KISSED Herbal Tea Bar", short_description: "Thoughtfully selected herbal teas to complement your in-clinic wellness visit." },
];

const destOf = (o) => o.square_payment_url || o.square_appointment_url || o.internal_route || "";
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

const ServiceCard = ({ s, testid }) => (
  <div className="rounded-2xl p-6 bg-white flex flex-col" style={{ border: `1px solid ${C.line}` }} data-testid={testid}>
    <div className="text-lg font-semibold text-slate-900" style={SERIF}>{s.title}</div>
    <p className="mt-2 text-sm text-slate-600 flex-1">{s.short_description}</p>
    {s.price_display ? <p className="mt-2 text-sm font-medium text-slate-800">{s.price_display}</p> : null}
    {destOf(s) ? (
      <a href={destOf(s)} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: C.primary }} data-testid={`book-${slug(s.title)}`}>
        {s.cta_label || "Book"} <ArrowRight className="h-4 w-4" />
      </a>
    ) : null}
  </div>
);

const CategoryHeader = ({ letter, title, copy }) => (
  <div className="mb-6">
    <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}>{letter}</div>
    <h2 className="mt-1 text-2xl sm:text-3xl text-slate-900" style={SERIF}>{title}</h2>
    {copy && <p className="mt-2 text-slate-600 max-w-3xl">{copy}</p>}
  </div>
);

const DirectedTreatments = () => (
  <div className="rounded-2xl p-7" style={{ background: C.lightBg, border: `1px solid ${C.line}` }} data-testid="directed-treatments">
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest" style={{ color: C.accent }}><Lock className="h-3.5 w-3.5" /> Assessment required first</div>
    <h3 className="mt-2 text-2xl text-slate-900" style={SERIF}>{DIRECTED_TREATMENTS.title}</h3>
    <p className="mt-3 text-sm text-slate-600 max-w-3xl">{DIRECTED_TREATMENTS.copy}</p>
    <ul className="mt-4 grid sm:grid-cols-2 gap-2">
      {DIRECTED_TREATMENTS.items.map((t) => <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 mt-0.5" style={{ color: C.green }} />{t}</li>)}
    </ul>
    <div className="mt-6"><PrimaryBtn to={DIRECTED_TREATMENTS.ctaTo} data-testid="directed-treatments-cta">{DIRECTED_TREATMENTS.ctaLabel} <ArrowRight className="h-4 w-4" /></PrimaryBtn></div>
  </div>
);

const ExistingClients = ({ hdr, existing }) => (
  <div id="existing-client-services" className="mt-16 scroll-mt-28" data-testid="existing-client-services">
    <CategoryHeader letter="D · For Existing Clients" title={hdr.existingHeading} copy={hdr.existingCopy} />
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {existing.map((o) => (
        <div key={o.id || o.title} className="rounded-2xl p-6 bg-white flex flex-col" style={{ border: `1px solid ${C.line}` }} data-testid={`existing-card-${slug(o.title)}`}>
          <div className="text-lg font-semibold text-slate-900" style={SERIF}>{o.title}</div>
          <p className="mt-2 text-sm text-slate-600">{o.short_description}</p>
          <ul className="mt-3 space-y-1.5 flex-1">
            {existingDetails(o.title).map((d) => <li key={d} className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: C.green }} />{d}</li>)}
          </ul>
          {o.price_display ? <p className="mt-3 text-sm font-medium text-slate-800">{o.price_display}</p> : null}
          {destOf(o) ? (
            <a href={destOf(o)} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: C.primary }} data-testid={`existing-book-${o.id || slug(o.title)}`}>{o.cta_label || "Book"} <ArrowRight className="h-4 w-4" /></a>
          ) : null}
        </div>
      ))}
    </div>
  </div>
);

export default function InClinicServices() {
  const hdr = useSiteContent("pages", DEFAULT_PAGES).services || DEFAULT_PAGES.services;
  const [clinical, setClinical] = useState(FALLBACK_SERVICES);
  const [existing, setExisting] = useState([]);

  useEffect(() => {
    api.get("/clinic-offers").then(({ data }) => {
      const offers = data.offers || [];
      const cs = offers.filter((o) => o.type === "clinical_service");
      if (cs.length) setClinical(cs);
      setExisting(offers.filter((o) => o.type === "existing_client_service"));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (window.location.hash && existing.length) document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [existing.length]);

  const { assessments, wellness } = splitClinical(clinical);

  return (
    <PublicLayout>
      <section style={{ background: C.lightBg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Eyebrow>{hdr.eyebrow}</Eyebrow>
          <h1 className="text-4xl sm:text-5xl leading-[1.05] text-slate-900 max-w-3xl" style={SERIF} data-testid="services-h1">{hdr.heading}</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">{hdr.subcopy}</p>
          <p className="mt-4 text-sm text-slate-500" data-testid="services-hsa-note">{HSA_NOTE}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <CategoryHeader letter="A · New Clients Start Here" title="Hair & Scalp Assessments" copy="An initial assessment helps determine appropriate next steps. It does not guarantee a diagnosis, outcome or specific treatment." />
        <div className="grid sm:grid-cols-2 gap-5" data-testid="category-assessments">
          {assessments.map((s) => <ServiceCard key={s.id || s.title} s={s} testid={`service-${slug(s.title)}`} />)}
        </div>

        <div className="mt-16">
          <CategoryHeader letter="B · After Your Assessment" title="Treatment Plans" />
          <DirectedTreatments />
        </div>

        <div className="mt-16">
          <CategoryHeader letter="C · General Wellness" title="Wellness Services" copy="In-clinic wellness services are supportive and non-diagnostic. KISSED refers clients to licensed medical providers when appropriate." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="category-wellness">
            {wellness.map((s) => <ServiceCard key={s.id || s.title} s={s} testid={`service-${slug(s.title)}`} />)}
          </div>
        </div>

        {existing.length > 0 && <ExistingClients hdr={hdr} existing={existing} />}

        <div className="mt-12 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ background: C.lightBg }}>
          <p className="text-slate-800" style={SERIF}>Not sure whether to start virtually or in person?</p>
          <GhostBtn to={ROUTES.navigator} data-testid="services-free-navigator">Take the Free New Client Assessment</GhostBtn>
        </div>
      </section>
    </PublicLayout>
  );
}
